/**
 * Pengajuan & Persetujuan — koreksi absensi (2026-10-12, Modul Kehadiran Tahap 3).
 *
 * - Karyawan: ajukan koreksi (5 jenis), lihat status + alasan keputusan, revisi
 *   saat diminta perbaikan, batalkan selagi pending.
 * - Pemeriksa: buka bukti (foto event + lampiran), setujui / tolak / minta
 *   perbaikan dgn alasan wajib.
 * - Log asli tidak diubah; perubahan efektif hanya setelah persetujuan (backend).
 * - Semua teks Bahasa Indonesia.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FilePlus2, CheckCircle2, XCircle, AlertTriangle, Loader2, Paperclip,
  RotateCcw, Ban, ChevronDown, ChevronUp, Clock, ShieldAlert,
} from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SearchableSelect from "@/components/SearchableSelect";

const KINDS = [
  { value: "forgot_in", label: "Lupa Absen Masuk" },
  { value: "forgot_out", label: "Lupa Absen Pulang" },
  { value: "outside_location", label: "Tugas di Luar Lokasi Penugasan" },
  { value: "camera_issue", label: "Kamera/Lokasi/Perangkat Bermasalah" },
  { value: "record_error", label: "Kesalahan Catatan Absensi" },
  { value: "exemption", label: "Pengecualian Absensi" },
];
const STATUS_META = {
  submitted: { label: "Menunggu Persetujuan", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  approved: { label: "Disetujui", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  rejected: { label: "Ditolak", cls: "text-rose-700 bg-rose-50 border-rose-200" },
  revision_requested: { label: "Diminta Perbaikan", cls: "text-sky-700 bg-sky-50 border-sky-200" },
  cancelled: { label: "Dibatalkan", cls: "text-slate-500 bg-slate-50 border-slate-200" },
};

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtD(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("id-ID", { weekday: "short", day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "text-slate-500 bg-slate-50 border-slate-200" };
  return <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 ${m.cls}`} data-testid={`corr-status-${status}`}>{m.label}</span>;
}

function CorrectionForm({ onSaved, onCancel }) {
  const [f, setF] = useState({ work_date: "", kind: "forgot_in", requested_time: "", requested_value: "", reason: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const needTime = f.kind === "forgot_in" || f.kind === "forgot_out";
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("work_date", f.work_date);
      fd.append("kind", f.kind);
      if (f.requested_time) fd.append("requested_time", f.requested_time);
      if (f.requested_value) fd.append("requested_value", f.requested_value);
      fd.append("reason", f.reason);
      if (file) fd.append("attachment", file);
      await api.post("/attendance/corrections", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pengajuan koreksi terkirim ke pemeriksa.");
      onSaved();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal mengirim pengajuan."); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="corr-form">
      <div className="font-display text-sm text-[#0B1D3A]">Ajukan Koreksi Absensi</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <Input type="date" value={f.work_date} max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setF({ ...f, work_date: e.target.value })} data-testid="corr-date" />
        <SearchableSelect value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={KINDS}
          placeholder="Jenis pengajuan" testid="corr-kind" />
        {needTime && (
          <Input type="time" value={f.requested_time} onChange={(e) => setF({ ...f, requested_time: e.target.value })}
            data-testid="corr-time" title="Waktu yang diajukan" />
        )}
        <Input placeholder="Nilai/waktu yang diajukan (opsional, mis. deskripsi koreksi)" value={f.requested_value}
          onChange={(e) => setF({ ...f, requested_value: e.target.value })} className="md:col-span-3" data-testid="corr-value" />
        <Textarea placeholder="Alasan pengajuan *" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}
          rows={2} className="md:col-span-3 rounded-none" data-testid="corr-reason" />
        <label className="md:col-span-3 inline-flex items-center gap-2 text-[11px] text-slate-600 border border-dashed border-slate-300 px-3 py-2 cursor-pointer hover:border-[#0B1D3A]" data-testid="corr-attach-label">
          <Paperclip size={12} />
          {file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "Lampiran opsional (foto/PDF, maks 10 MB)"}
          <input type="file" accept="image/*,application/pdf" className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="corr-attach" />
        </label>
      </div>
      <div className="text-[10px] text-slate-500 italic">
        Log absensi asli tidak diubah. Perubahan efektif hanya setelah pemeriksa menyetujui.
        Tanggal yang sudah tercakup Cuti/Izin aktif tidak dapat dikoreksi di sini.
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !f.work_date || !f.reason.trim() || (needTime && !f.requested_time)}
          className="rounded-none bg-[#0B1D3A]" data-testid="corr-submit">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <FilePlus2 size={12} />} Kirim Pengajuan
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="rounded-none" data-testid="corr-cancel">Batal</Button>
      </div>
    </div>
  );
}

function DecisionBox({ corr, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    if ((action === "reject" || action === "revise") && !reason.trim()) {
      toast.error("Alasan keputusan wajib diisi.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/attendance/corrections/${corr.id}/decide`, { action, reason: reason.trim() || null });
      toast.success(action === "approve" ? "Pengajuan disetujui — data efektif diperbarui."
        : action === "reject" ? "Pengajuan ditolak." : "Perbaikan diminta ke pemohon.");
      onDone();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal memproses keputusan."); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-t border-slate-100 mt-2 pt-2 space-y-2" data-testid="corr-decision-box">
      <Textarea placeholder="Alasan keputusan (wajib untuk tolak / minta perbaikan)" value={reason}
        onChange={(e) => setReason(e.target.value)} rows={2} className="rounded-none text-xs" data-testid="corr-decision-reason" />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => decide("approve")} disabled={busy} className="rounded-none bg-emerald-700 hover:bg-emerald-800" data-testid="corr-approve-btn">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Setujui
        </Button>
        <Button size="sm" onClick={() => decide("reject")} disabled={busy} className="rounded-none bg-rose-600 hover:bg-rose-700" data-testid="corr-reject-btn">
          <XCircle size={12} /> Tolak
        </Button>
        <Button size="sm" variant="outline" onClick={() => decide("revise")} disabled={busy} className="rounded-none" data-testid="corr-revise-btn">
          <RotateCcw size={12} /> Minta Perbaikan
        </Button>
      </div>
    </div>
  );
}

function CorrectionCard({ corr, meId, canDecide, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [revising, setRevising] = useState(false);
  const [rf, setRf] = useState({ requested_time: corr.requested_time || "", requested_value: corr.requested_value || "", reason: corr.reason || "" });
  const [rfile, setRfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const needTime = corr.kind === "forgot_in" || corr.kind === "forgot_out";

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      try { const r = await api.get(`/attendance/corrections/${corr.id}`); setDetail(r.data); }
      catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    }
  };
  const resubmit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("requested_time", rf.requested_time || "");
      fd.append("requested_value", rf.requested_value || "");
      fd.append("reason", rf.reason);
      if (rfile) fd.append("attachment", rfile);
      await api.post(`/attendance/corrections/${corr.id}/resubmit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pengajuan direvisi & dikirim ulang ke pemeriksa.");
      setRevising(false); onChanged();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Batalkan pengajuan ini?")) return;
    try { await api.post(`/attendance/corrections/${corr.id}/cancel`); toast.success("Pengajuan dibatalkan."); onChanged(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid={`corr-card-${corr.id}`}>
      <button type="button" onClick={toggle} className="w-full px-3 py-2 flex items-center justify-between gap-3 text-left hover:bg-slate-50" data-testid={`corr-toggle-${corr.id}`}>
        <div className="min-w-0">
          <div className="text-xs font-semibold text-[#0B1D3A] truncate">{corr.kind_label} <span className="text-slate-400 font-normal">· {fmtD(corr.work_date)}</span></div>
          <div className="text-[10px] text-slate-500 truncate">
            {corr.requested_time && <span className="font-mono">Diajukan {corr.requested_time} · </span>}
            Pemeriksa: {corr.reviewer_name || "—"}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={corr.status} />
          {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 text-[11px]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500">Alasan Pemohon</div>
              <div className="text-slate-700 whitespace-pre-wrap">{corr.reason}</div>
              {corr.requested_value && <div className="mt-1 text-slate-600">Nilai diajukan: {corr.requested_value}</div>}
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500">Keputusan</div>
              {corr.decided_at ? (
                <div className="text-slate-700">
                  <StatusBadge status={corr.status} /> oleh {corr.decided_by_name || "—"} · {fmtDT(corr.decided_at)}
                  {corr.decision_reason && <div className="mt-1 text-slate-600" data-testid={`corr-decision-reason-${corr.id}`}>Alasan: {corr.decision_reason}</div>}
                </div>
              ) : <div className="text-slate-400 italic">Belum diputuskan</div>}
            </div>
          </div>
          {/* Bukti */}
          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-2">
            {corr.has_attachment && (
              <a href={corr.attachment_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0B1D3A] underline" data-testid={`corr-attach-open-${corr.id}`}>
                <Paperclip size={11} /> Buka Lampiran
              </a>
            )}
            {detail?.event?.photo_in_url && (
              <a href={detail.event.photo_in_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0B1D3A] underline" data-testid={`corr-photo-in-${corr.id}`}>
                Foto Absen Masuk
              </a>
            )}
            {detail?.event?.photo_out_url && (
              <a href={detail.event.photo_out_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0B1D3A] underline">
                Foto Absen Pulang
              </a>
            )}
            {detail?.event && (
              <span className="text-slate-500">
                Catatan asli: masuk {detail.event.clock_in_at ? fmtDT(detail.event.clock_in_at) : "—"} · pulang {detail.event.clock_out_at ? fmtDT(detail.event.clock_out_at) : "—"}
              </span>
            )}
          </div>
          {(corr.revision_history || []).length > 0 && (
            <div className="text-[10px] text-slate-500">
              Riwayat revisi: {corr.revision_history.length}x · terakhir {fmtDT(corr.revision_history[corr.revision_history.length - 1].at)}
            </div>
          )}
          {/* Aksi pemohon */}
          {corr.user_id === meId && (corr.status === "submitted" || corr.status === "revision_requested") && (
            <div className="flex items-center gap-2 border-t border-slate-100 pt-2">
              {corr.status === "revision_requested" && (
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setRevising(!revising)} data-testid={`corr-revise-open-${corr.id}`}>
                  <RotateCcw size={12} /> Revisi Pengajuan
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-none text-rose-600" onClick={cancel} data-testid={`corr-cancel-${corr.id}`}>
                <Ban size={12} /> Batalkan
              </Button>
            </div>
          )}
          {revising && (
            <div className="border border-sky-200 bg-sky-50 p-3 space-y-2" data-testid={`corr-revise-form-${corr.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-sky-800 font-semibold">Revisi Pengajuan</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {needTime && <Input type="time" value={rf.requested_time} onChange={(e) => setRf({ ...rf, requested_time: e.target.value })} data-testid={`corr-revise-time-${corr.id}`} />}
                <Input placeholder="Nilai yang diajukan (opsional)" value={rf.requested_value} onChange={(e) => setRf({ ...rf, requested_value: e.target.value })} />
                <Textarea placeholder="Alasan *" value={rf.reason} onChange={(e) => setRf({ ...rf, reason: e.target.value })} rows={2} className="md:col-span-2 rounded-none" data-testid={`corr-revise-reason-${corr.id}`} />
                <label className="md:col-span-2 inline-flex items-center gap-2 text-[10px] text-slate-600 border border-dashed border-slate-300 px-3 py-1.5 cursor-pointer">
                  <Paperclip size={10} /> {rfile ? rfile.name : "Ganti lampiran (opsional)"}
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setRfile(e.target.files?.[0] || null)} />
                </label>
              </div>
              <Button size="sm" onClick={resubmit} disabled={busy || !rf.reason.trim() || (needTime && !rf.requested_time)} className="rounded-none bg-[#0B1D3A]" data-testid={`corr-resubmit-${corr.id}`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Kirim Ulang
              </Button>
            </div>
          )}
          {/* Aksi pemeriksa */}
          {canDecide && corr.status === "submitted" && <DecisionBox corr={corr} onDone={onChanged} />}
        </div>
      )}
    </div>
  );
}

export default function PengajuanPersetujuanPanel() {
  const [view, setView] = useState("mine");
  const [mine, setMine] = useState([]);
  const [review, setReview] = useState([]);
  const [me, setMe] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [noReviewer, setNoReviewer] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [m, rv, meR, metaR] = await Promise.all([
        api.get("/attendance/corrections?role=mine"),
        api.get("/attendance/corrections?role=review").catch(() => ({ data: [] })),
        api.get("/auth/me"),
        api.get("/attendance/meta"),
      ]);
      setMine(m.data || []); setReview(rv.data || []); setMe(meR.data); setMeta(metaR.data);
      // Deteksi pemeriksa belum dikonfigurasi: banner muncul hanya bila TIDAK
      // ada satu pun penugasan ber-reviewer DAN tidak ada pemeriksa default global.
      const [asgR, stR] = await Promise.all([
        api.get("/attendance/assignments?active_only=true").catch(() => ({ data: [] })),
        api.get("/attendance/settings").catch(() => ({ data: {} })),
      ]);
      const anyReviewer = (asgR.data || []).some((a) => a.reviewer_user_id);
      setNoReviewer(!anyReviewer && !(stR.data || {}).default_reviewer_user_id);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat pengajuan."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pendingReview = useMemo(() => review.filter((c) => c.status === "submitted" && c.user_id !== me?.id), [review, me]);
  const canDecideAny = pendingReview.length > 0 || !!meta?.can_approve;

  if (loading) return <div className="p-6 text-center text-slate-500 text-xs"><Loader2 className="inline animate-spin" size={14} /> Memuat pengajuan…</div>;

  return (
    <div className="space-y-3" data-testid="att-pengajuan-panel">
      {noReviewer && (
        <div className="border border-rose-200 bg-rose-50 px-3 py-2 flex items-start gap-2" data-testid="corr-no-reviewer">
          <ShieldAlert size={14} className="text-rose-700 mt-0.5 shrink-0" />
          <div className="text-[11px] text-rose-800">
            <span className="font-semibold uppercase tracking-wider">Pemeriksa belum dikonfigurasi. </span>
            Pengajuan tidak dapat dikirim sampai HR mengisi kolom Pemeriksa pada penugasan Anda atau mengatur Pemeriksa Default di Pengaturan → Koreksi &amp; Bukti.
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 border-b border-slate-200">
          <button type="button" onClick={() => setView("mine")} data-testid="corr-view-mine"
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 ${view === "mine" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500"}`}>
            Pengajuan Saya ({mine.length})
          </button>
          <button type="button" onClick={() => setView("review")} data-testid="corr-view-review"
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 ${view === "review" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500"}`}>
            Perlu Persetujuan ({pendingReview.length})
          </button>
        </div>
        {view === "mine" && (
          <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => setShowForm(!showForm)} data-testid="corr-new-btn">
            <FilePlus2 size={14} /> Ajukan Koreksi
          </Button>
        )}
      </div>
      {showForm && view === "mine" && <CorrectionForm onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}
      <div className="space-y-2">
        {(view === "mine" ? mine : review).length === 0 && (
          <div className="border border-slate-200 px-3 py-6 text-center text-slate-400 italic text-xs">
            {view === "mine" ? "Belum ada pengajuan koreksi." : "Tidak ada pengajuan yang perlu Anda periksa."}
          </div>
        )}
        {(view === "mine" ? mine : review).map((c) => (
          <CorrectionCard key={c.id} corr={c} meId={me?.id}
            canDecide={view === "review" && canDecideAny && c.user_id !== me?.id}
            onChanged={load} />
        ))}
      </div>
      <div className="text-[10px] text-slate-500 flex items-center gap-1">
        <Clock size={10} /> Keputusan pemeriksa memperbarui data absensi efektif; catatan asli tetap tersimpan dan dapat diaudit.
      </div>
    </div>
  );
}
